import React from 'react';
import { FormattedMessage } from 'react-intl';
import { PropTypes } from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { List, ListSubheader } from '@material-ui/core';
import { useMeContext } from '~/modules/me';
import { NoDataItem } from '~/modules/common/components';
import SkillDetails from './SkillDetails';
import UserDefinedSkillList from './UserDefinedSkillList';
import { transformSuggestionToSkillAssignment } from './hooks/skillTransformationUtils';
import CompanySkillTitle from './CompanySkillTitle';

const NoSkillsAssigned = () => (
  <NoDataItem>
    <FormattedMessage id="skillInfoCard.noSkillsAssigned" />
  </NoDataItem>
);

const useStyles = makeStyles(theme => ({
  content: {
    color: theme.palette.text.primary
  },
  contentRoot: {
    borderTop: `1px solid ${theme.palette.divider}`
  },
  title: {
    fontSize: theme.typography.h6.fontSize,
    color: theme.palette.text.secondary
  },
  skillName: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.caption.fontSize,
    lineHeight: 1,
    padding: theme.spacing(1, 0, 1, 0),
    backgroundColor: theme.palette.background.paper
  },
  suggestionsContainer: {
    marginBottom: theme.spacing(1)
  },
  noDateItem: {
    paddingTop: theme.spacing(2)
  }
}));

const skillDetailsKey = (category, skillAssignment) => {
  const skillAssignmentKey = skillAssignment.uri || skillAssignment.displayText;

  return `${category.uri}|${skillAssignmentKey}`;
};

const createCategoryFromSuggestion = suggestion => ({
  uri: suggestion.categoryUri,
  displayText: suggestion.categoryName
});

export const getSkillHeader = ({ classes, skill, index }) => (
  <ListSubheader
    data-qe-id={`ReadOnlySkillCategory_${index + 1}`}
    className={classes.skillName}
  >
    {skill?.category ? (
      skill.category.displayText
    ) : (
      <FormattedMessage id="skillInfoCard.unCategorized" />
    )}
  </ListSubheader>
);

export const SkillsReadOnly = ({
  certificates,
  classes: classesOverride,
  skills,
  canEditSkills,
  userDefinedSkills,
  userDefinedSkillSuggestions,
  systemLevelSkillSuggestions,
  maxSkillLevel,
  onAcceptUserNudges,
  onRejectUserNudges,
  onAcceptUserDefinedSkills,
  userUri
}) => {
  const {
    featureFlags: { isPsaPraaUserProfileNudgesEnabled }
  } = useMeContext();
  const classes = useStyles({ classes: classesOverride });

  const hasUserDefinedContent =
    (userDefinedSkills && userDefinedSkills.length > 0) ||
    (isPsaPraaUserProfileNudgesEnabled &&
      userDefinedSkillSuggestions &&
      userDefinedSkillSuggestions.length > 0);

  const hasSystemSkillContent =
    skills?.length > 0 || systemLevelSkillSuggestions?.length > 0;
  const hasAnyContent = hasSystemSkillContent || hasUserDefinedContent;

  const existingCategoryUris = skills
    ? skills.map(skill => skill.category?.uri).filter(Boolean)
    : [];

  const uncategorizedSystemSuggestions = isPsaPraaUserProfileNudgesEnabled
    ? (systemLevelSkillSuggestions || []).filter(
        suggestion => !existingCategoryUris.includes(suggestion.categoryUri)
      )
    : [];

  return (
    <div className={classes.content}>
      {hasAnyContent ? (
        <>
          <UserDefinedSkillList
            userDefinedSkills={userDefinedSkills}
            userDefinedSkillSuggestions={userDefinedSkillSuggestions}
            maxSkillLevel={maxSkillLevel}
            certificates={certificates}
            canEditSkills={canEditSkills}
            onRejectUserNudges={onRejectUserNudges}
            onAcceptUserDefinedSkills={onAcceptUserDefinedSkills}
            userUri={userUri}
          />
          {isPsaPraaUserProfileNudgesEnabled && hasUserDefinedContent && (
            <CompanySkillTitle />
          )}
          {skills.map((skill, index) => {
            const systemLevelAssignments = skill.skillAssignments || [];

            const categorySystemSuggestions = isPsaPraaUserProfileNudgesEnabled
              ? (systemLevelSkillSuggestions || [])
                  .filter(
                    suggestion => suggestion.categoryUri === skill.category?.uri
                  )
                  .map(transformSuggestionToSkillAssignment)
              : [];

            return systemLevelAssignments.length > 0 ||
              categorySystemSuggestions.length > 0 ? (
              <div key={skill.category.uri}>
                <List
                  data-qe-id={`ReadOnlySkillList_${index + 1}`}
                  subheader={getSkillHeader({ classes, skill, index })}
                >
                  {categorySystemSuggestions.map((skillAssignment, index1) => (
                    <SkillDetails
                      dataQeId={`${
                        skill.category.displayText
                      }_Suggestion_${index1 + 1}`}
                      key={
                        isPsaPraaUserProfileNudgesEnabled
                          ? skillDetailsKey(skill.category, skillAssignment)
                          : skillAssignment.displayText
                      }
                      skillAssignment={skillAssignment}
                      maxSkillLevel={maxSkillLevel}
                      certificates={certificates}
                      category={skill.category}
                      canEditSkills={canEditSkills}
                      onAcceptUserNudges={onAcceptUserNudges}
                      onRejectUserNudges={onRejectUserNudges}
                    />
                  ))}

                  {systemLevelAssignments.map((skillAssignment, index1) => (
                    <SkillDetails
                      dataQeId={`${
                        skill.category.displayText
                      }_Category_Skill_${index1 + 1}`}
                      key={
                        isPsaPraaUserProfileNudgesEnabled
                          ? skillDetailsKey(skill.category, skillAssignment)
                          : skillAssignment.displayText
                      }
                      skillAssignment={skillAssignment}
                      maxSkillLevel={maxSkillLevel}
                      certificates={certificates}
                      category={skill.category}
                      canEditSkills={canEditSkills}
                      onAcceptUserNudges={onAcceptUserNudges}
                      onRejectUserNudges={onRejectUserNudges}
                    />
                  ))}
                </List>
              </div>
            ) : null;
          })}
          {uncategorizedSystemSuggestions.length > 0 && (
            <List data-qe-id="UncategorizedSystemSuggestions">
              {uncategorizedSystemSuggestions.map((suggestion, index) => (
                <SkillDetails
                  key={`uncategorized-suggestion-${suggestion.nudgeId}`}
                  dataQeId={`UncategorizedSuggestion_${index + 1}`}
                  maxSkillLevel={maxSkillLevel}
                  skillAssignment={transformSuggestionToSkillAssignment(
                    suggestion
                  )}
                  certificates={certificates}
                  category={createCategoryFromSuggestion(suggestion)}
                  canEditSkills={canEditSkills}
                  onAcceptUserNudges={onAcceptUserNudges}
                  onRejectUserNudges={onRejectUserNudges}
                />
              ))}
            </List>
          )}
          {isPsaPraaUserProfileNudgesEnabled &&
            hasUserDefinedContent &&
            !hasSystemSkillContent && (
              <div className={classes.noDateItem}>
                <NoSkillsAssigned />
              </div>
            )}
        </>
      ) : (
        <NoSkillsAssigned />
      )}
    </div>
  );
};

SkillsReadOnly.propTypes = {
  skills: PropTypes.array.isRequired,
  canEditSkills: PropTypes.bool,
  userDefinedSkills: PropTypes.array,
  userDefinedSkillSuggestions: PropTypes.array,
  systemLevelSkillSuggestions: PropTypes.array,
  classes: PropTypes.object,
  maxSkillLevel: PropTypes.number,
  certificates: PropTypes.array,
  onAcceptUserNudges: PropTypes.func.isRequired,
  onRejectUserNudges: PropTypes.func.isRequired,
  onAcceptUserDefinedSkills: PropTypes.func,
  userUri: PropTypes.string
};

export default SkillsReadOnly;
